import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Text, Card, CardBody, Table, Thead, Tbody, Tr, Th, Td,
  HStack, Button, IconButton, Badge, useColorModeValue, useToast, VStack, StackDivider
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, RepeatIcon } from '@chakra-ui/icons';
import { Icon } from '@chakra-ui/react';
import { MdPowerSettingsNew } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { designationsAPI } from '../services/api';

// Define Designation interface for type safety
interface Designation {
  _id: number;
  title: string;
  code: string;
  department?: string;
  grade: string;
  description?: string;
  status: 'active' | 'inactive';
}

const Designations: React.FC = () => {
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const toast = useToast();
  const navigate = useNavigate();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const fetchDesignations = async () => {
    try {
      const res = await designationsAPI.getAll();
      setDesignations(res.data);
    } catch (err: any) {
      console.error('Fetch designations error:', err.message || err);
      toast({
        title: 'Error',
        description: 'Failed to fetch designations. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      setDesignations([]);
    }
  };

  useEffect(() => {
    fetchDesignations();
  }, []);

  const toggleStatus = async (designation: Designation) => {
    const id = designation._id;
    if (id === undefined || id === null) {
      toast({
        title: 'Error',
        description: 'Invalid designation id',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    const newStatus = designation.status === 'active' ? 'inactive' : 'active';
    setLoadingId(id);
    try {
      const res = await designationsAPI.update(id.toString(), { status: newStatus });
      const updatedDesignation = res.data;
      // Validate response data
      if (!updatedDesignation || typeof updatedDesignation !== 'object' || !updatedDesignation._id || typeof updatedDesignation.status !== 'string') {
        throw new Error('Invalid response data from server');
      }
      setDesignations(prev => prev.map(d => (d._id === updatedDesignation._id ? updatedDesignation : d)));
      toast({
        title: 'Status Updated',
        description: `${updatedDesignation.title} is now ${updatedDesignation.status}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err: any) {
      console.error('Toggle status error:', err);
      const msg = err?.response?.data?.message || err.message || 'Failed to update status. Please try again.';
      toast({
        title: 'Failed to Update Status',
        description: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (id?: number) => {
    try {
      if (id === undefined || id === null) {
        toast({
          title: 'Error',
          description: 'Invalid designation id',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }
      await designationsAPI.softDelete(id.toString());
      toast({
        title: 'Designation Deleted',
        description: 'Designation deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      fetchDesignations();
    } catch (err: any) {
      toast({
        title: 'Failed to Delete Designation',
        description: 'Failed to delete designation. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleEdit = (id?: number) => {
    if (id === undefined || id === null) {
      toast({
        title: 'Error',
        description: 'Invalid designation id',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    designationsAPI.getById(id.toString())
      .then((res: any) => {
        const d = res.data;
        navigate(`/designations/edit/${id}`);
        // toast({
        //   title: 'Navigated to Edit Designation',
        //   description: `Editing details for ${d.title}.`,
        //   status: 'info',
        //   duration: 3000,
        //   isClosable: true,
        //   position: 'top-right',
        // });
      })
      .catch((err: any) => {
        toast({
          title: 'Error',
          description: 'Failed to fetch designation details.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const handleAddDesignation = () => {
    navigate('/designations/add');
    // toast({
    //   title: 'Navigated to Add Designation',
    //   description: 'You have been redirected to the add designation page.',
    //   status: 'info',
    //   duration: 3000,
    //   isClosable: true,
    //   position: 'top-right',
    // });
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'green' : 'gray';
  };

  return (
    <Box w="full" px={{ base: 2, md: 6 }}>
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>
          Designations
        </Heading>
        <Text color="gray.600">
          Manage designation titles and grades
        </Text>
      </Box>

      <Box mb={6}>
        <HStack
          justify="space-between"
          flexWrap={{ base: 'wrap', md: 'nowrap' }}
          gap={{ base: 2, md: 4 }}
        >
          <Button
            leftIcon={<AddIcon />}
            colorScheme="primary"
            onClick={handleAddDesignation}
            className="btn-primary"
            w={{ base: '100%', md: 'auto' }}
          >
            Add Designation
          </Button>
        </HStack>
      </Box>

      <Box overflowX="auto" w="full">
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody p={0}>
            <Table variant="simple" minWidth="900px">
              <Thead>
                <Tr>
                  <Th className="table-header">ID</Th>
                  <Th className="table-header">Title</Th>
                  <Th className="table-header">Code</Th>
                  <Th className="table-header">Department</Th>
                  <Th className="table-header">Grade</Th>
                  <Th className="table-header">Description</Th>
                  <Th className="table-header text-center" textAlign="center">Status</Th>
                  <Th className="table-header">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {designations.length === 0 ? (

                  //conflict
                  <Tr>
                    <Td colSpan={8} textAlign="center">
                      <Text color="gray.500">No designations found</Text>
                    </Td>
                    <Th className="table-header" minWidth="80px" maxWidth="110px">ID</Th>
                    <Th className="table-header" minWidth="120px" maxWidth="180px">Title</Th>
                    <Th className="table-header" minWidth="90px" maxWidth="120px">Code</Th>
                    <Th className="table-header" minWidth="100px" maxWidth="140px">Department</Th>
                    <Th className="table-header" minWidth="90px" maxWidth="120px">Grade</Th>
                    <Th className="table-header" minWidth="120px" maxWidth="180px">Description</Th>
                    <Th className="table-header text-center" textAlign="center" minWidth="70px" maxWidth="90px">Status</Th>
                    <Th className="table-header" minWidth="60px" maxWidth="80px">Actions</Th>
                  </Tr>

                  //conflict
                ) : (
                  designations.map(d => (
                    <Tr key={d._id}>
                      <Td fontSize={{ base: 'sm', md: 'md' }}>{d._id}</Td>
                      <Td fontSize={{ base: 'sm', md: 'md' }}>{d.title}</Td>
                      <Td fontSize={{ base: 'sm', md: 'md' }}>{d.code}</Td>
                      <Td fontSize={{ base: 'sm', md: 'md' }}>{d.department || '—'}</Td>
                      <Td fontSize={{ base: 'sm', md: 'md' }}>{d.grade}</Td>
                      <Td fontSize={{ base: 'sm', md: 'md' }}>{d.description || 'N/A'}</Td>
                      <Td textAlign="center">
                        <Badge
                          colorScheme={getStatusColor(d.status)}
                          variant="subtle"
                          px={{ base: 2, md: 3 }}
                          py={1}
                          textTransform="capitalize"
                          fontSize={{ base: 'xs', md: 'sm' }}
                          borderRadius="md"
                          minW={{ base: '60px', md: '80px' }}
                          textAlign="center"
                          display="inline-block"
                        >
                          {d.status}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={{ base: 1, md: 2 }}>
                          <IconButton
                            aria-label="Toggle status"
                            icon={<RepeatIcon />}
                            size={{ base: 'xs', md: 'sm' }}
                            variant="ghost"
                            onClick={() => toggleStatus(d)}
                            isLoading={loadingId === d._id}
                            isDisabled={loadingId !== null && loadingId !== d._id}
                            title={d.status === 'active' ? 'Deactivate' : 'Activate'}
                            sx={{ color: d.status === 'active' ? 'red' : 'green' }}
                          />
                          <IconButton
                            aria-label="Edit designation"
                            icon={<EditIcon />}
                            size={{ base: 'xs', md: 'sm' }}
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleEdit(d._id)}
                          />
                          <IconButton
                            aria-label="Delete designation"
                            icon={<DeleteIcon />}
                            size={{ base: 'xs', md: 'sm' }}
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDelete(d._id)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};

export default Designations;